import { BusinessLabelChips } from "./BusinessLabelChips";
import { parseLabelNames } from "constants/sideLoadedBusinesses";
import {
  Input,
  SelectField,
  SelectFieldOption,
  TextArea,
} from "components/Form/FormikControls";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getBusinessById } from "selectors";
import { TableCell, TableRow } from "@material-ui/core";
import styled from "styled-components/macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSideLoadedBusinessMappableOptionsById,
  getSideLoadedBusinessById,
} from "selectors/sideLoadedBusinesses";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import {
  FieldConfiguration,
  FieldGroup,
  NOTES_FIELD_INDICES,
} from "./fieldConfigurations";
import { Row } from "components/Layout";
import {
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "selectors/indexes";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { Error } from "components/Form/FormikControls/Error";

import { useMountEffect } from "hooks";

export interface SideLoadedFieldRowProps {
  configuration: FieldConfiguration;
  businessId?: string;
  recordId: string;
}

const TableRowStatus = {
  ValidAndMapped: "ValidAndMapped",
  InvalidAndMapped: "InvalidAndMapped",
  UnMapped: "UnMapped",
};

export const ActiveTableRow = styled(TableRow)`
  background-color: ${({ status, theme }) => {
    switch (status) {
      case TableRowStatus.ValidAndMapped:
        return theme.colors.lightGreen;
      case TableRowStatus.InvalidAndMapped:
        return theme.colors.error;
      case TableRowStatus.UnMapped:
        return theme.colors.gray85;
    }
  }};
`;

interface MappableFieldSelectorProps {
  name: string;
  recordId: string;
  asChips?: boolean;
}

const OptionHeading = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
  color: ${({ theme }) => theme.colors.titleLight};
  text-transform: uppercase;
`;

function formatOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <div>{option.label}</div>
    </div>
  );
}

function formatLabelsOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <BusinessLabelChips names={parseLabelNames(option.label)} />
    </div>
  );
}

function MappableFieldSelector({
  name,
  recordId,
  asChips,
}: MappableFieldSelectorProps) {
  const options = useSelector(
    getSideLoadedBusinessMappableOptionsById(recordId),
  );
  return (
    <SelectField
      name={name}
      options={options}
      placeholder="Not mapped..."
      isClearable
      formatOptionLabel={asChips ? formatLabelsOptionLabel : formatOptionLabel}
    />
  );
}

function BusinessLookup({ valueFieldName }) {
  const valueField = useField(valueFieldName)[0];

  const businessIdByAgentCodeLookup = useSelector(
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );
  const businessIdBySaleyardCodeLookup = useSelector(
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );

  // Try and pull it out of the lookup.
  const businessIdFromAgentCode = businessIdByAgentCodeLookup[valueField.value];
  const businessIdFromSaleyardCode =
    businessIdBySaleyardCodeLookup[valueField.value];

  const businessId = businessIdFromAgentCode || businessIdFromSaleyardCode;

  const mappedBusiness = useSelector(getBusinessById(businessId)) || null;

  if (!mappedBusiness) {
    return (
      <div>
        No business found for {valueField.value}.{" "}
        <Tooltip title="This value needs to map to the Agent Code or Saleyard Code of an existing business - does a business with that code exist?" />
      </div>
    );
  }

  return (
    <div>
      {mappedBusiness.name}{" "}
      <Tooltip
        title={`Found business from ${businessIdFromAgentCode ? "Agent Code" : "Saleyard Code"}`}
      />
    </div>
  );
}

function NotesValueEditor({
  configuration,
  recordId,
}: {
  configuration: FieldConfiguration;
  recordId: string;
}) {
  const mappableFieldName = `MappableField__${configuration.value}`;
  const valueFieldName = `Value__${configuration.value}`;

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const notesFieldKeys = useMemo(
    () =>
      NOTES_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      ),
    [configuration.value],
  );

  const { values } = useFormikContext();

  // Snapshot of only the note dropdown values so we can depend on them without listing N variables
  const noteValuesSignature = JSON.stringify(
    notesFieldKeys.map(k => values[k]),
  );

  const [valueFieldField, , valueFieldHelpers] = useField(valueFieldName);
  const setValueFieldValue = valueFieldHelpers.setValue;

  const mappableFieldHelpers = useField(mappableFieldName)[2];
  const setMappableFieldValue = mappableFieldHelpers.setValue;

  // Only recompute when the note dropdown selections change, not on every form keystroke
  const defaultNotesValue = useMemo(() => {
    const noteValues = JSON.parse(noteValuesSignature) as (
      | string
      | undefined
    )[];
    return noteValues
      .filter(
        (fieldName: string) =>
          fieldName !== undefined &&
          sideLoadedBusiness.rawData[fieldName] !== undefined,
      )
      .map(
        fieldName => `${fieldName}: ${sideLoadedBusiness.rawData[fieldName]}`,
      )
      .join("\n");
  }, [sideLoadedBusiness, noteValuesSignature]);

  useEffect(() => {
    // If the default notes change, because a user changes the drop down, set the input value to the new default (this does override a users changes.)
    setValueFieldValue(defaultNotesValue);
    // And make sure the mappable field is set, too.
    setMappableFieldValue(true);
  }, [defaultNotesValue, setValueFieldValue, setMappableFieldValue]);

  useMountEffect(() => {
    // Set the value, if it's defined, on first load.
    if (defaultNotesValue) {
      setValueFieldValue(defaultNotesValue);
    }
  });

  return (
    <React.Fragment>
      {notesFieldKeys.map(fieldName => (
        <MappableFieldSelector
          name={fieldName}
          recordId={recordId}
          key={fieldName}
        />
      ))}
      <MappableFieldValueDisplay
        mappableFieldValue={valueFieldField.value}
        valueFieldName={valueFieldName}
        asTextArea
      />
    </React.Fragment>
  );
}

interface MappableFieldValueDisplayProps {
  mappableFieldValue: string;
  valueFieldName: string;
  isBusinessLookup?: boolean;
  asTextArea?: boolean;
}
function MappableFieldValueDisplay({
  mappableFieldValue,
  valueFieldName,
  isBusinessLookup,
  asTextArea,
}: MappableFieldValueDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);

  const [valueField, valueMeta] = useField(valueFieldName);

  if (mappableFieldValue === null) {
    return null;
  }

  if (isBusinessLookup) {
    // BusinessLookup says which value it couldn't resolve, which is the same
    // failure the validation reports.
    return <BusinessLookup valueFieldName={valueFieldName} />;
  }

  if (isEditing) {
    // Input and TextArea render the error themselves.
    if (asTextArea) {
      return (
        <TextArea name={valueFieldName} rows="8" label="" errorColor="black" />
      );
    } else {
      return (
        <Input name={valueFieldName} disableAutoComplete errorColor="black" />
      );
    }
  }

  return (
    <>
      <span onClick={() => setIsEditing(true)} className="cursor-pointer">
        <FontAwesomeIcon icon={faPencil} />
        &nbsp;{asTextArea ? <pre>{valueField.value}</pre> : valueField.value}
      </span>
      {valueMeta.error && <Error color="black">{valueMeta.error}</Error>}
    </>
  );
}

function SideLoadedFieldRowInner({
  configuration,
  businessId,
  recordId,
}: SideLoadedFieldRowProps) {
  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const valueFieldName = `Value__${configuration.value}`;
  const mappableFieldName = `MappableField__${configuration.value}`;

  const valueFieldElements = useField(valueFieldName);
  const valueFieldValue = valueFieldElements[0].value;
  const valueMeta = valueFieldElements[1];
  const valueFieldHelpers = valueFieldElements[2];
  const mappableFieldValue = useField(mappableFieldName)[0].value;

  const setValueFieldValue = valueFieldHelpers.setValue;

  const { handleAsNotes } = configuration;

  // Notes keep their mappable field switched on so the editor stays reachable,
  // so it's their content, not the flag, that says whether anything is mapped.
  const isMapped = handleAsNotes
    ? Boolean(valueFieldValue)
    : mappableFieldValue !== null;

  let rowStatus = TableRowStatus.UnMapped;
  if (isMapped) {
    if (valueMeta.error) {
      rowStatus = TableRowStatus.InvalidAndMapped;
    } else {
      rowStatus = TableRowStatus.ValidAndMapped;
    }
  }

  useEffect(() => {
    // Notes handle their own state/changes.
    if (!handleAsNotes) {
      if (mappableFieldValue === null) {
        setValueFieldValue("");
      } else {
        setValueFieldValue(sideLoadedBusiness.rawData[mappableFieldValue]);
      }
    }
  }, [
    mappableFieldValue,
    sideLoadedBusiness,
    setValueFieldValue,
    handleAsNotes,
  ]);

  const BusinessValueRenderer = configuration.businessValueRenderer || null;
  const existingValue = BusinessValueRenderer ? (
    <BusinessValueRenderer businessId={businessId} />
  ) : null;
  return (
    <ActiveTableRow status={rowStatus}>
      <TableCell>
        <strong>{configuration.label}</strong>
        <br />
        <i>{existingValue}</i>
      </TableCell>
      <TableCell>
        {configuration.handleAsNotes ? (
          <NotesValueEditor configuration={configuration} recordId={recordId} />
        ) : (
          <React.Fragment>
            <MappableFieldSelector
              name={mappableFieldName}
              recordId={recordId}
              asChips={configuration.handleAsLabels}
            />
            <MappableFieldValueDisplay
              mappableFieldValue={mappableFieldValue}
              valueFieldName={valueFieldName}
              isBusinessLookup={configuration.isBusinessLookup}
            />
          </React.Fragment>
        )}
      </TableCell>
    </ActiveTableRow>
  );
}

export function SideLoadedFieldGroupHeadingRow({
  group,
}: {
  group: FieldGroup;
}) {
  return (
    <TableRow>
      <TableCell colSpan={2}>
        <Row marginLeft={4}>
          <h2>{group}</h2>
        </Row>
      </TableCell>
    </TableRow>
  );
}

export const SideLoadedFieldRow = React.memo(SideLoadedFieldRowInner);
